import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopCategory = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const categoryNames = {
    'signage': 'Signage',
    'acrylic-displays': 'Acrylic Displays',
    'menu-stands': 'Menu Stands',
    'promotional-items': 'Promotional Items',
    'large-format': 'Large Format',
    'fabricated-letters': 'Fabricated Letters',
    'vehicle-branding': 'Vehicle Branding',
    'lightboxes': 'Lightboxes',
  };

  useEffect(() => {
    fetchData();
  }, [categoryId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Try to fetch category from database
      const catResponse = await axios.get(`${API}/shop-categories/${categoryId}`);
      setCategory(catResponse.data);
      
      // Fetch products for this category
      const prodResponse = await axios.get(`${API}/shop-products?category_id=${categoryId}`);
      setProducts(prodResponse.data);
    } catch (error) {
      // If category not found in database, use fallback name
      setCategory({ id: categoryId, name: categoryNames[categoryId] || 'Products' });
      // Generate placeholder products
      setProducts(generatePlaceholderProducts(categoryId));
    } finally {
      setLoading(false);
    }
  };

  const generatePlaceholderProducts = (catId) => {
    const catName = categoryNames[catId] || 'Product';
    return Array.from({ length: 20 }, (_, i) => ({
      id: `${catId}-product-${i + 1}`,
      name: `${catName} Item ${i + 1}`,
      description: `High-quality ${catName.toLowerCase()} product with premium materials and expert craftsmanship.`,
      base_price: Math.floor(Math.random() * 5000) + 500,
      image: '',
      variants: [
        { name: 'Small', dimensions: '300mm x 200mm', price_add: 0 },
        { name: 'Medium', dimensions: '500mm x 350mm', price_add: 200 },
        { name: 'Large', dimensions: '800mm x 600mm', price_add: 500 },
        { name: 'Extra Large', dimensions: '1200mm x 900mm', price_add: 800 },
      ],
      in_stock: Math.random() > 0.2,
    }));
  };

  const getDisplayPrice = (product) => {
    if (user && user.role === 'customer') {
      // Use custom diamond_price if set, otherwise 10% off base price
      if (product.diamond_price && product.diamond_price > 0) {
        return product.diamond_price;
      }
      return product.base_price * 0.9;
    }
    return product.base_price;
  };

  if (loading) {
    return <div className="pt-32 px-6 text-center">Loading...</div>;
  }

  const categoryName = category?.name || categoryNames[categoryId] || 'Products';

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        <Button
          onClick={() => navigate('/shop')}
          variant="ghost"
          className="mb-6 text-white/70 hover:text-white"
        >
          <ArrowLeft className="mr-2" size={20} /> Back to Shop
        </Button>

        <h1 className="text-5xl md:text-6xl font-black mb-6" data-testid="category-title">{categoryName}</h1>
        <p className="text-xl text-white/70 mb-12">Browse our {categoryName} collection</p>

        {products.length === 0 ? (
          <div className="text-center py-20 border border-white/10">
            <p className="text-white/50 text-lg">No products in this category yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => {
              const displayPrice = getDisplayPrice(product);
              const hasDiscount = user && user.role === 'customer';
              const discountLabel = product.diamond_price ? 'DIAMOND' : '-10%';

              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.03 }}
                  onClick={() => navigate(`/shop/product/${product.id}`)}
                  className="group overflow-hidden border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 cursor-pointer flex flex-col bg-white/5"
                  data-testid={`product-card-${index}`}
                >
                  {/* Title Above Image */}
                  <div className="p-4 bg-[#0a0a0a]">
                    <h3 className="text-lg font-bold text-white/70 group-hover:text-[#D4AF37] transition-colors">
                      {product.name}
                    </h3>
                  </div>
                  
                  <div className="w-full h-80 bg-black/20 flex items-center justify-center overflow-hidden">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt="" 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="text-white/30">
                        <ShoppingCart size={48} />
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 flex-1 flex flex-col bg-[#0a0a0a]">
                    <p className="text-white/60 text-sm mb-3 flex-1 whitespace-pre-line">{product.description}</p>
                    
                    {product.variants && product.variants.length > 0 && (
                      <div className="text-xs text-white/50 mb-2">
                        Variants: {product.variants.map(v => v.name).join(', ')}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      {product.base_price > 0 && (
                        <div>
                          {hasDiscount && (
                            <span className="text-xs text-white/40 line-through mr-2">R{product.base_price?.toFixed(2)}</span>
                          )}
                          <span className="text-xl font-bold text-[#D4AF37]">
                            R{displayPrice?.toFixed(2)}
                          </span>
                          {hasDiscount && (
                            <span className="ml-2 text-xs bg-[#D4AF37] text-black px-2 py-1 font-bold">{discountLabel}</span>
                          )}
                        </div>
                      )}
                      {product.in_stock !== false ? (
                        <span className="text-xs text-green-400">In Stock</span>
                      ) : product.lead_time ? (
                        <span className="text-xs text-[#D4AF37]">Lead Time: {product.lead_time}</span>
                      ) : (
                        <span className="text-xs text-red-400">Out of Stock</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Coming Soon Notice */}
        <div className="mt-12 p-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-lg text-center">
          <p className="text-[#D4AF37] font-bold mb-1">More Products Coming Soon</p>
          <p className="text-white/60 text-sm">We're continuously adding new products to this category. Check back regularly for updates!</p>
        </div>
      </motion.div>
    </div>
  );
};

export default ShopCategory;
